<template>
  <div class="editable-text">
    <!-- Display mode: the whole text is the click target.

         The distinct keys on the two branches are load-bearing. Without them Vue reuses
         the editor's <div> as this one and just swaps its click handler. A real browser
         re-renders in the middle of dispatching a click, so the click that pressed Cancel
         kept bubbling into that same element, now wired to startEdit, and reopened the
         editor at once: Cancel, and Save with nothing changed, appeared to do nothing.
         Keyed, the editor element is replaced rather than reused, and the new element is
         not on the in-flight click's path. -->
    <div
      v-if="!editing"
      key="display"
      class="editable-text__display"
      :title="$t('userPrompts.clickToEdit')"
      @click="startEdit"
    >
      <span v-if="hasValue" class="editable-text__value">{{ value }}</span>
      <span v-else class="editable-text__empty">{{ emptyText }}</span>
      <i class="el-icon-edit editable-text__icon"></i>
    </div>

    <!-- Edit mode. click.stop keeps clicks in the editor from re-selecting the table row. -->
    <div v-else key="editor" class="editable-text__editor" @click.stop>
      <el-input
        ref="input"
        v-model="draft"
        type="textarea"
        :autosize="{ minRows: 2, maxRows: 6 }"
        :maxlength="maxlength"
        show-word-limit
        :placeholder="placeholder"
        @keydown.native="onKeydown"
      ></el-input>
      <div class="editable-text__actions">
        <span class="editable-text__hint">{{ $t('userPrompts.editHint') }}</span>
        <el-button size="mini" class="editable-text__cancel" @click="cancel">{{ $t('common.cancel') }}</el-button>
        <el-button
          size="mini"
          type="primary"
          class="editable-text__save"
          :loading="saving"
          @click="commit"
        >{{ $t('common.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Click-to-edit text cell for the prompt library table.
 *
 * The parent passes `save(text)`, which must return a Promise. The editor closes only when
 * it resolves; on rejection it stays open with the admin's draft intact, so a failed save
 * (network error, server validation) never throws away what they typed. The axios
 * interceptor already shows the server's error message, so nothing is toasted here.
 */
export default {
  name: 'EditableText',
  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    // Shown in place of the text when the value is empty, so there is still something to click
    emptyText: { type: String, default: '' },
    maxlength: { type: Number, default: 500 },
    // Required fields reject a blank draft instead of saving it
    required: { type: Boolean, default: false },
    save: { type: Function, required: true }
  },
  data() {
    return {
      editing: false,
      draft: '',
      saving: false
    }
  },
  computed: {
    hasValue() {
      return !!(this.value && this.value.trim())
    }
  },
  methods: {
    startEdit() {
      this.draft = this.value || ''
      this.editing = true
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus()
      })
    },
    cancel() {
      if (this.saving) return
      this.editing = false
      this.draft = ''
    },
    onKeydown(e) {
      if (e.key === 'Escape') {
        e.preventDefault()
        this.cancel()
      } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
        this.commit()
      }
    },
    commit() {
      if (this.saving) return
      const next = (this.draft || '').trim()

      if (this.required && !next) {
        this.$message.warning(this.$t('userPrompts.emptyPrompt'))
        return
      }
      // Nothing changed: close without a round trip
      if (next === (this.value || '').trim()) {
        this.cancel()
        return
      }

      this.saving = true
      Promise.resolve(this.save(next))
        .then(() => {
          this.editing = false
          this.draft = ''
        })
        .catch(() => {
          // Keep the editor open so the draft survives the failure
        })
        .finally(() => {
          this.saving = false
        })
    }
  }
}
</script>

<style scoped>
.editable-text__display {
  position: relative;
  min-height: 22px;
  padding: 4px 22px 4px 6px;
  border-radius: 4px;
  cursor: pointer;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
  transition: background-color 0.15s;
}
.editable-text__display:hover {
  background-color: #ecf5ff;
}
.editable-text__empty {
  color: #c0c4cc;
  font-style: italic;
}
.editable-text__icon {
  position: absolute;
  top: 7px;
  right: 4px;
  color: #409eff;
  opacity: 0;
  transition: opacity 0.15s;
}
.editable-text__display:hover .editable-text__icon {
  opacity: 1;
}
.editable-text__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 6px;
}
.editable-text__hint {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  line-height: 1.4;
  color: #909399;
  /* el-table cells set break-all, which split the hint mid-word ("Esc to ca / ncel") */
  word-break: normal;
}
.editable-text__actions .el-button + .el-button {
  margin-left: 0;
}
</style>
